"use client";

import { c as _c } from "react/compiler-runtime";
import { fullScreenSupported } from "@/helpers/Helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import useCapacitor from "@/hooks/useCapacitor";
import { faExpand, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import React, { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";
import useKeyPressEvent from "react-use/lib/useKeyPressEvent";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
const tooltipProps = {
  delayShow: 250,
  place: "top" as const,
  style: {
    backgroundColor: "#37373E",
    color: "white",
    zIndex: 1002
  }
};
const modalButtonClasses = "tw-flex tw-items-center tw-justify-center tw-border-0 tw-text-iron-50 tw-bg-iron-800 desktop-hover:hover:tw-bg-iron-700 tw-rounded-full tw-size-10 tw-flex-shrink-0 tw-backdrop-blur-sm tw-transition-all tw-duration-300 tw-ease-out";
function NaturalHeightImage(t0) {
  const $ = _c(11);
  const {
    imgRef,
    primarySrc,
    fallbackSrc,
    retryTick,
    onLoad,
    onFinalError
  } = t0;
  const [currentSrc, setCurrentSrc] = useState(primarySrc);
  const [usedFallback, setUsedFallback] = useState(false);
  let t1;
  if ($[0] !== fallbackSrc || $[1] !== onFinalError || $[2] !== usedFallback) {
    t1 = () => {
      if (!usedFallback) {
        setCurrentSrc(fallbackSrc);
        setUsedFallback(true);
        return;
      }
      onFinalError();
    };
    $[0] = fallbackSrc;
    $[1] = onFinalError;
    $[2] = usedFallback;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleError = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      objectPosition: "center"
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== currentSrc || $[6] !== handleError || $[7] !== imgRef || $[8] !== onLoad || $[9] !== retryTick) {
    t3 = <img key={retryTick} ref={imgRef} src={currentSrc} alt="Drop media" loading="lazy" className="tw-block tw-h-auto tw-w-full tw-max-w-full tw-object-contain" style={t2} onLoad={onLoad} onError={handleError} />;
    $[5] = currentSrc;
    $[6] = handleError;
    $[7] = imgRef;
    $[8] = onLoad;
    $[9] = retryTick;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
export default function WaveDropPartContentFullWidthImage(t0) {
  const $ = _c(40);
  const {
    src,
    imageScale: t1
  } = t0;
  const imageScale = t1 === undefined ? ImageScale.AUTOx450 : t1;
  const [loaded, setLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [retryTick, setRetryTick] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const imgRef = useRef(null);
  const modalImageRef = useRef(null);
  const {
    isCapacitor
  } = useCapacitor();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setLoaded(true);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const handleImageLoad = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setErrorCount(1);
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const handleError = t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setLoaded(false);
      setErrorCount(0);
      setRetryTick(_temp);
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const manualRetry = t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = event => {
      event.stopPropagation();
      setIsModalOpen(true);
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const handleOpenModal = t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsModalOpen(false);
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const handleCloseModal = t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = event_0 => {
      event_0.stopPropagation();
      const fullscreenTarget = modalImageRef.current ?? imgRef.current;
      if (fullscreenTarget) {
        fullscreenTarget.requestFullscreen().catch(_temp2);
      }
    };
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const handleFullScreen = t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      handleCloseModal();
    };
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  useKeyPressEvent("Escape", t8);
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <button type="button" aria-label="Close modal" onClick={handleCloseModal} className="tw-fixed tw-inset-0 tw-z-1000 tw-border-0 tw-bg-black/80 tw-p-0" />;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      disabled: true
    };
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const t11 = !isZoomed;
  let t12;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = event_1 => setIsZoomed(event_1.state.scale > 1);
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  let t13;
  if ($[10] !== isCapacitor || $[11] !== isZoomed || $[12] !== src) {
    t13 = t14 => {
      const {
        resetTransform
      } = t14;
      return <div className="tw-pointer-events-none tw-fixed tw-inset-0 tw-z-[1001] tw-flex tw-items-center tw-justify-center tw-overflow-hidden"><div className="tw-pointer-events-auto tw-relative tw-flex tw-max-h-[90vh] tw-max-w-[95vw] tw-flex-col lg:tw-flex-row"><div className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-items-center tw-justify-center"><TransformComponent wrapperClass="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center" contentClass="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center"><img ref={modalImageRef} src={src} alt="Full size drop media" style={{
                pointerEvents: "auto"
              }} className="tw-max-h-[75vh] tw-max-w-full tw-object-contain lg:tw-max-h-[90vh]" /></TransformComponent></div><div className="tw-fixed tw-right-4 tw-top-2 tw-z-[1001] tw-flex tw-flex-row tw-gap-x-4 tw-pt-[env(safe-area-inset-top,0px)] lg:tw-relative lg:tw-right-auto lg:tw-top-0 lg:tw-ml-4 lg:tw-flex-col-reverse lg:tw-gap-x-0 lg:tw-gap-y-2 lg:tw-self-start lg:tw-pt-0">{isZoomed && <button type="button" onClick={event_2 => {
              event_2.stopPropagation();
              resetTransform();
              setIsZoomed(false);
            }} data-tooltip-id={`reset-zoom-${src}`} className={modalButtonClasses} aria-label="Reset"><FontAwesomeIcon icon={faRotateLeft} className="tw-size-4" /></button>}<Link href={src} target="_blank" rel="noopener noreferrer" data-tooltip-id={`open-browser-${src}`} className={modalButtonClasses} aria-label="Open image in new tab"><ArrowTopRightOnSquareIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" /></Link>{fullScreenSupported() && !isCapacitor && <button type="button" onClick={handleFullScreen} data-tooltip-id={`full-screen-${src}`} className={modalButtonClasses} aria-label="Full screen"><FontAwesomeIcon icon={faExpand} className="tw-size-4" /></button>}<button type="button" onClick={handleCloseModal} data-tooltip-id={`close-modal-${src}`} className={modalButtonClasses} aria-label="Close modal"><svg className="tw-h-5 tw-w-5 tw-flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button></div></div></div>;
    };
    $[10] = isCapacitor;
    $[11] = isZoomed;
    $[12] = src;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== t11 || $[15] !== t13) {
    t14 = <TransformWrapper panning={t10} limitToBounds={t11} smooth={true} onZoom={t12}>{t13}</TransformWrapper>;
    $[14] = t11;
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== isCapacitor || $[18] !== src) {
    t15 = !isCapacitor && <><Tooltip id={`open-browser-${src}`} {...tooltipProps}><span className="tw-text-xs">Open in Browser</span></Tooltip><Tooltip id={`full-screen-${src}`} {...tooltipProps}><span className="tw-text-xs">Full screen</span></Tooltip><Tooltip id={`reset-zoom-${src}`} {...tooltipProps}><span className="tw-text-xs">Reset zoom</span></Tooltip><Tooltip id={`close-modal-${src}`} {...tooltipProps}><span className="tw-text-xs">Close</span></Tooltip></>;
    $[17] = isCapacitor;
    $[18] = src;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t14 || $[21] !== t15) {
    t16 = <div className="tailwind-scope tw-relative tw-z-1000 tw-cursor-default">{t9}{t14}{t15}</div>;
    $[20] = t14;
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const modalContent = t16;
  if (errorCount > 0) {
    let t17;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-8"><span className="tw-text-sm tw-text-iron-400">Couldn’t load image.</span><button type="button" onClick={manualRetry} className="tw-rounded-md tw-bg-iron-700 tw-px-3 tw-py-1 tw-text-xs tw-text-white hover:tw-bg-iron-600">Retry</button></div>;
      $[23] = t17;
    } else {
      t17 = $[23];
    }
    return t17;
  }
  let t17;
  if ($[24] !== imageScale || $[25] !== src) {
    t17 = getScaledImageUri(src, imageScale);
    $[24] = imageScale;
    $[25] = src;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== retryTick || $[28] !== src || $[29] !== t17) {
    t18 = <button type="button" onClick={handleOpenModal} aria-label="Open drop media" className="tw-block tw-w-full tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0"><NaturalHeightImage key={retryTick} imgRef={imgRef} primarySrc={t17} fallbackSrc={src} retryTick={retryTick} onLoad={handleImageLoad} onFinalError={handleError} /></button>;
    $[27] = retryTick;
    $[28] = src;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== loaded) {
    t19 = !loaded && <div className="tw-sr-only" aria-live="polite">Loading image</div>;
    $[31] = loaded;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== isModalOpen || $[34] !== modalContent) {
    t20 = isModalOpen && createPortal(modalContent, document.body);
    $[33] = isModalOpen;
    $[34] = modalContent;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t18 || $[37] !== t19 || $[38] !== t20) {
    t21 = <>{t18}{t19}{t20}</>;
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  return t21;
}
function _temp2() {}
function _temp(currentTick) {
  return currentTick + 1;
}